import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styles from '../../styles/cr.module.scss';
import { IWithErrorHandlingProps, withErrorHandling } from '../withErrorHandling';
import { SignOffType } from '../../refData/SignOffType';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrCommandBar } from '../cr/CrCommandBar';
import { SignOffList } from '../signOff/SignOffList';
import { SignOffPageSettings } from '../project/SignOffPageSettings';
import { Period } from '../../refData/Period';
import { DataContext } from '../DataContext';
import { IEntityDescription1 } from '../../types';
import { decodeRouteId, encodeRouteId } from '../../utils/routeId';

interface ProgrammeSignOffProps extends IWithErrorHandlingProps {
    lastDirectorateIdForChilds?: number;
    apiConnected: boolean;
    emphasiseProjectsWithCustomField: string;
    emphasisedProjectsHeaderText: string;
    otherProjectsHeaderText: string;

    lastPortfolioIdForChilds?: number | null;
    setLastPortfolioIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;
    onEntityDescriptionChange: (desc: IEntityDescription1) => void;
}

const ProgrammeSignOff = (props: ProgrammeSignOffProps) => {
    const { dataServices: { programmeService, reportDueDatesService } } = useContext(DataContext);
    const [showOptionsAndSettings, setShowOptionsAndSettings] = useState<boolean>(false);
    const [updatePeriod, setUpdatePeriod] = useState<Date>(null);
    const [uniqueUpdatePeriods, setUniqueUpdatePeriods] = useState<Date[]>(null);
    const [showPreviousUpdate, setShowPreviousUpdate] = useState<string>('false');
    const [selectedReportPeriod, setSelectedReportPeriod] = useState<Period>(Period.Current);

    const { programmeIdForUpdates: programmeIdForUpdates_encoded } = useParams<{ programmeIdForUpdates: string }>();
    const programmeIdForUpdates = decodeRouteId(programmeIdForUpdates_encoded) ?? programmeIdForUpdates_encoded;

    const { requestUpdatePeriod } = useParams<{ requestUpdatePeriod: string }>();

    useEffect(() => {
        if (requestUpdatePeriod) {
            setUpdatePeriod(new Date(requestUpdatePeriod));
        }
    }, [requestUpdatePeriod]);

    useEffect(() => {
        const getUniqueDates = async () => {
            if (Number(programmeIdForUpdates) > 0) {
                const uniqueDates = await reportDueDatesService.getProgrammeUpdateUniqueDates(Number(programmeIdForUpdates));
                setUniqueUpdatePeriods(uniqueDates);
            }
        };
        getUniqueDates();

        programmeService.readTitleForProgramme('Programme Updates', 'Programme Update', Number(programmeIdForUpdates)).then(ed => {
            props.onEntityDescriptionChange(ed);
        });
    }, [programmeIdForUpdates, reportDueDatesService]);

    const history = useHistory();

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'programmes',
            text: 'Back to Programmes',
            iconProps: { iconName: 'Back' },
            onClick: (ev, item) => {
                //console.log('props.lastPortfolioIdForChilds', props.lastPortfolioIdForChilds);
                if (props.lastPortfolioIdForChilds != null) {
                    history.push(`/a2/programmes-for-portfolio/${encodeRouteId(props.lastPortfolioIdForChilds!)}`);
                }
                else {
                    history.push('/a2/programmes');
                }
            },
        },
        {
            key: 'OptionsSettings',
            text: 'Options & Settings',
            iconProps: { iconName: 'MiniExpand' },
            onClick: () => setShowOptionsAndSettings(true),
        },
    ];

    const handleChangeShowPreviousUpdate = (value: string) => {
        setShowPreviousUpdate(value);
    };

    const handleChangeSelectedPeriod = (period: Period) => {
        if (period === Period.Current || period === Period.Previous) {
            setUpdatePeriod(null);
        }
        setSelectedReportPeriod(period);
    };

    const handleChangeUpdatePeriod = (dateVal: Date) => {
        if (selectedReportPeriod === Period.Specific) {
            setUpdatePeriod(dateVal);
        } else {
            setUpdatePeriod(null);
        }
    };

    return (
        <div className={styles.updatesContainer}>
            <CrCommandBar items={commandBarItems} />
            <div>
                <SignOffList
                    defaultReportType={SignOffType.Programme}
                    defaultEntityId={Number(programmeIdForUpdates)}
                    {...props}
                />
            </div>
            <SignOffPageSettings
                showForm={showOptionsAndSettings}
                showPeriodSelection={true}
                onClosed={() => setShowOptionsAndSettings(false)}
                showPreviousUpdate={showPreviousUpdate}
                onChangeShowPreviousUpdate={handleChangeShowPreviousUpdate}
                reportPeriod={selectedReportPeriod}
                onChangePeriod={handleChangeSelectedPeriod}
                uniqueUpdatePeriods={uniqueUpdatePeriods}
                updatePeriod={updatePeriod}
                onChangeUpdatePeriod={handleChangeUpdatePeriod}
            />
        </div>
    );
};

export default withErrorHandling(ProgrammeSignOff);
